import React, { useState } from 'react';
import { Code2, Palette, Cpu } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

// ─── Data ─────────────────────────────────────────────────────────────────────

const TEAM = [
  {
    name: 'Tega Emmanuel',
    role: 'Founder & CEO',
    title: 'Lead Software Engineer',
    description:
      'Leads our technology and product development, making sure everything we build is fast, reliable, and designed to serve users well.',
    image: '/images/Tee.png',
    accent: 'orange',
    icon: Cpu,
    tags: ['Programmer', 'Security', 'Architecture'],
    social: {
      instagram: '#',
      email: 'tega@builderslab.net',
    },
  },
  {
    name: 'Kosisochukwu Mbanugo',
    role: 'Senior Developer',
    title: 'Web Developer & Designer',
    description:
      'Specialises in creating beautiful, user-friendly designs that combine strong development skills with clean, precise visuals.',
    image: '/images/f8ea0bd9e_kosi.png',
    accent: 'blue',
    icon: Palette,
    tags: ['UI/UX', 'Design Systems', 'Motion'],
    social: {
      instagram: '#',
      email: 'kosi@builderslab.net',
    },
  },
  {
    name: 'Okike Princewill',
    role: 'Web Developer',
    title: 'Full Stack Developer',
    description:
      'Builds fast, easy-to-use, and responsive websites using modern web technologies with strong attention to detail.',
    image: '/images/princewill.png',
    accent: 'violet',
    icon: Code2,
    tags: ['React', 'Performance', 'Security'],
    social: {
      instagram: '#',
      email: 'princewill@builderslab.net',
    },
  },
  {
    name: 'Praise Iwuozor',
    role: 'Web Developer',
    title: 'Frontend Developer',
    description:
      'Creating beautiful, user-friendly designs that combine strong development skills with clean, precise visuals.',
    image: '/images/praise.png',
    accent: 'blue',
    icon: Palette,
    tags: ['React', 'UI/UX', 'Design Systems'],
    social: {
      instagram: '#',
      email: 'praise@builderslab.net',
    },
  },
];

const ACCENT = {
  orange: {
    badge:   'bg-orange-500/15 text-orange-400 border-orange-500/25',
    glow:    'shadow-[0_0_60px_rgba(249,115,22,0.12)]',
    ring:    'ring-orange-500/40',
    tag:     'bg-orange-500/10 text-orange-400/80 border-orange-500/15',
    icon:    'text-orange-400 bg-orange-500/10',
    hover:   'hover:border-orange-500/40',
  },
  blue: {
    badge:   'bg-blue-500/15 text-blue-400 border-blue-500/25',
    glow:    'shadow-[0_0_60px_rgba(59,130,246,0.12)]',
    ring:    'ring-blue-500/40',
    tag:     'bg-blue-500/10 text-blue-400/80 border-blue-500/15',
    icon:    'text-blue-400 bg-blue-500/10',
    hover:   'hover:border-blue-500/40',
  },
  violet: {
    badge:   'bg-violet-500/15 text-violet-400 border-violet-500/25',
    glow:    'shadow-[0_0_60px_rgba(139,92,246,0.12)]',
    ring:    'ring-violet-500/40',
    tag:     'bg-violet-500/10 text-violet-400/80 border-violet-500/15',
    icon:    'text-violet-400 bg-violet-500/10',
    hover:   'hover:border-violet-500/40',
  },
};

// ─── Member Card ──────────────────────────────────────────────────────────────

function MemberCard({ member, index }) {
  const [hovered, setHovered] = useState(false);
  const a = ACCENT[member.accent];
  const Icon = member.icon;

  return (
    <div
     
     
     
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative group flex flex-col rounded-3xl border border-zinc-800 bg-zinc-900/60 backdrop-blur-sm
        p-8 transition-all duration-300 ${a.hover} ${hovered ? a.glow : ''}`}
    >
      {/* Subtle grid texture */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl overflow-hidden opacity-30">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTSAwIDIwIEwgMjAgMCIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3N2Zz4=')] bg-repeat" />
      </div>

      {/* Role badge */}
      <div className="flex justify-center mb-6 z-10 relative">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${a.badge}`}>
          {member.role}
        </span>
      </div>

      {/* Avatar */}
      <div className="flex justify-center mb-6">
        <div
         
         
          className={`relative w-28 h-28 rounded-2xl overflow-hidden ring-2 ${a.ring} ring-offset-2 ring-offset-zinc-900`}
        >
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover"
            onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.classList.add('bg-zinc-800');
            }}
          />
          {/* Subtle shine overlay on hover */}
          <div
           
           
            className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"
          />
        </div>
      </div>

      {/* Name & title */}
      <div className="text-center mb-4">
        <h3 className="text-white font-bold text-xl tracking-tight mb-1">{member.name}</h3>
        <p className="text-zinc-400 text-sm font-medium">{member.title}</p>
      </div>

      {/* Description */}
      <p className="text-zinc-500 text-sm leading-relaxed text-center mb-6 flex-1">
        {member.description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap justify-center gap-1.5 mb-6">
        {member.tags.map(tag => (
          <span key={tag} className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border ${a.tag}`}>
            {tag}
          </span>
        ))}
      </div>

      {/* Divider */}
      <div className="h-px bg-white/5 mb-5" />

      {/* Social links */}
      <div className="flex justify-center gap-3">
        <a
          href={member.social.instagram}
          aria-label="Instagram"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-400 hover:text-white text-xs font-medium transition-all"
        >
          Follow
        </a>
        <a
          href={`mailto:${member.social.email}`}
          aria-label="Email"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-400 hover:text-white text-xs font-medium transition-all"
        >
          Email
        </a>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Team() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-20">
      <SEOHead
        title="Meet the Team"
        description="Meet the talented team behind BuildersLab — developers, designers, and strategists building the future of AI-powered web development."
        ogUrl="https://builderslab.net/Team"
      />

      {/* Background ambience */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden -z-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.06)_0%,transparent_70%)]" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.05)_0%,transparent_70%)]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16">

        {/* ── Header ── */}
        <div
         
         
         
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
            <span className="text-orange-400 text-sm font-medium tracking-wide">The Crew</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] mb-5">
            <span className="text-white">Built by</span>
            <br />
            <span className="bg-gradient-to-r from-orange-400 via-orange-300 to-amber-400 bg-clip-text text-transparent">
              obsessive builders.
            </span>
          </h1>

          <p className="text-zinc-400 text-lg max-w-xl mx-auto leading-relaxed font-light">
            We are a team of designers, engineers, and product thinkers. We believe the web should be fast, beautiful, and easy for people to use.
          </p>
        </div>

        {/* ── Cards grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEAM.map((member, i) => (
            <MemberCard key={member.name} member={member} index={i} />
          ))}
        </div>

        {/* ── Bottom CTA strip ── */}
        <div
         
         
         
          className="mt-20 text-center"
        >
          <div className="inline-flex flex-col items-center gap-4 px-8 py-8 rounded-3xl bg-zinc-900/60 border border-zinc-800 backdrop-blur-sm">
            <p className="text-zinc-400 text-sm">Want to build with us?</p>
            <a
              href="mailto:hello@builderslab.net"
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink font-semibold text-sm transition-all shadow-[0_4px_20px_rgba(249,115,22,0.35)] hover:shadow-[0_4px_28px_rgba(249,115,22,0.5)]"
            >
              Say hello →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}